import React, { useCallback, useEffect, useState } from "react";
import {
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import moment from "moment";
import pulseServices from "app/services/pulse-services";

// Licencias de App Conductor: cuantas compro cada cliente, cuantas personas condujeron, cuanta
// holgura usaron y a quien se bloqueo. Binario: no hay cobros proporcionales; pasada la holgura,
// la persona sin licencia no puede iniciar un viaje hasta que la empresa compre otra.
// Ver hermes-backend app/services/licensing/seats.rb y app/services/pulse/licenses.rb.
const LicensesCell = ({ row, onSaved }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(row.driver_licenses ?? "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await pulseServices.updateLicenses(row.company_id, value === "" ? null : Number(value));
            setEditing(false);
            onSaved();
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <span>{row.contracted ?? "Sin límite"}</span>
                {row.driver_licenses == null && row.contracted != null && (
                    <Typography variant="caption" color="text.secondary">(plan)</Typography>
                )}
                <Button size="small" onClick={() => setEditing(true)}>Cambiar</Button>
            </Stack>
        );
    }
    return (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <TextField size="small" type="number" value={value} onChange={(e) => setValue(e.target.value)}
                       placeholder="del plan" inputProps={{ min: 0 }} sx={{ width: 90 }} />
            <Button size="small" variant="contained" color="warning" disabled={saving} onClick={save}>Guardar</Button>
            <Button size="small" color="inherit" onClick={() => setEditing(false)}>Cancelar</Button>
        </Stack>
    );
};

const LicensesTab = () => {
    const [month, setMonth] = useState(moment().format("YYYY-MM"));
    const [data, setData] = useState(null);

    const load = useCallback(() => {
        pulseServices.licensesReport({ month: `${month}-01` }).then(setData).catch(() => setData({ data: [] }));
    }, [month]);

    useEffect(() => {
        setData(null);
        load();
    }, [load]);

    const rows = data?.data;

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Typography variant="h6">Licencias</Typography>
                    <TextField type="month" size="small" label="Mes" value={month} onChange={(e) => setMonth(e.target.value)} InputLabelProps={{ shrink: true }} />
                    {data && (
                        <Chip size="small" color={data.enforced ? "error" : "default"} variant="outlined"
                              label={data.enforced ? "Bloqueo activado" : "Modo de prueba: no se bloquea a nadie"} />
                    )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Cada licencia es una persona en el mes; las ocupan quienes más días condujeron. Los días del resto salen de la holgura
                    ({data?.holgura_dias ?? 10} días-conductor al mes por empresa). Sin holgura, la persona sin licencia no puede iniciar un viaje hasta
                    que la empresa compre otra. "Cambiar" ajusta las licencias compradas (vacío vuelve al cupo del plan).
                </Typography>
                {!rows ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress color="warning" /></Stack>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Cliente</TableCell>
                                    <TableCell>Avisos</TableCell>
                                    <TableCell align="right">Licencias</TableCell>
                                    <TableCell align="right">Personas que condujeron</TableCell>
                                    <TableCell align="right">Holgura usada</TableCell>
                                    <TableCell align="right">Bloqueos</TableCell>
                                    <TableCell align="right">Teléfonos usados</TableCell>
                                    <TableCell align="right">Cambios de teléfono</TableCell>
                                    <TableCell align="right">Intentos desde otro teléfono</TableCell>
                                    <TableCell>Entregas imposibles</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.company_id} hover>
                                        <TableCell>
                                            <b>{r.name}</b>
                                            {r.plan && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{r.plan}</Typography>}
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 220 }}>
                                            {r.alerts.length === 0
                                                ? <Chip size="small" color="success" variant="outlined" label="En regla" />
                                                : r.alerts.map((a) => <Chip key={a} size="small" color="warning" variant="outlined" label={a} sx={{ m: 0.25, height: "auto", "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 } }} />)}
                                        </TableCell>
                                        <TableCell align="right"><LicensesCell row={r} onSaved={load} /></TableCell>
                                        <TableCell align="right">
                                            {r.active_drivers}
                                            <Typography component="span" variant="caption" color="text.secondary"> (mes anterior {r.active_drivers_prev})</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {r.contracted == null ? "-" : (
                                                <Typography variant="body2" component="span" color={r.holgura_used >= r.holgura_total ? "error.main" : "text.primary"}>
                                                    {r.holgura_used} de {r.holgura_total}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {r.blocked}
                                            {r.would_block > 0 && <Typography component="span" variant="caption" color="text.secondary"> (+{r.would_block} en prueba)</Typography>}
                                        </TableCell>
                                        <TableCell align="right">{r.devices_used}</TableCell>
                                        <TableCell align="right">
                                            {r.switches}
                                            {r.approved_switches > 0 && <Typography component="span" variant="caption" color="text.secondary"> ({r.approved_switches} aprobados)</Typography>}
                                        </TableCell>
                                        <TableCell align="right">{r.session_moved}</TableCell>
                                        <TableCell>
                                            {r.impossible_jumps.length === 0 ? "-" : r.impossible_jumps.map((j) => (
                                                <Typography key={j.driver_id} variant="caption" sx={{ display: "block" }}>
                                                    {j.driver_name || `Conductor ${j.driver_id}`}: {j.count} (hasta {j.max_km} km en menos de 10 min)
                                                </Typography>
                                            ))}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {rows.length === 0 && (
                                    <TableRow><TableCell colSpan={10}><Typography color="text.secondary">Sin datos para este mes.</Typography></TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};

export default LicensesTab;
